import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, Users, Shield, Link2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const UseCases = () => {
  const useCases = [
    {
      icon: <TrendingUp size={32} className="text-[#0E7490]" />,
      color: 'teal',
      category: 'Retail Banking',
      title: 'Credit Scoring Enhancement',
      image: 'https://plus.unsplash.com/premium_photo-1682126272530-66eff2e1755d?auto=format&fit=crop&w=1200&q=80',
      challenge: 'Static credit bureau scores miss dynamic financial behavior',
      solution: 'Real-time transaction analysis delivers 100+ behavioral features',
      impact: [
        '35% improvement in default prediction',
        '20% increase in approval rates',
        'Faster credit decisions'
      ]
    },
    {
      icon: <Users size={32} className="text-[#4F46E5]" />,
      color: 'purple',
      category: 'NBFC & Business Lending',
      title: 'SME Underwriting Automation',
      image: 'https://plus.unsplash.com/premium_photo-1754262069332-624516730107?auto=format&fit=crop&w=1200&q=80',
      challenge: 'Manual GST and bank statement analysis takes 3-5 days',
      solution: 'AI agents triangulate data sources and generate CAM in under 15 minutes',
      impact: [
        '85% reduction in TAT',
        '100% audit traceability',
        '3x underwriter productivity'
      ]
    },
    {
      icon: <Shield size={32} className="text-[#1A56DB]" />,
      color: 'blue',
      category: 'Financial Services',
      title: 'Fraud Detection & RCU',
      image: 'https://plus.unsplash.com/premium_photo-1749319835930-cb58d145d428?auto=format&fit=crop&w=1200&q=80',
      challenge: 'Synthetic identity fraud bypasses traditional checks',
      solution: 'Multi-source forensic analysis with behavioral pattern detection',
      impact: [
        '90% fraud detection accuracy',
        'Real-time alert generation',
        'Reduced false positives'
      ]
    },
    {
      icon: <Link2 size={32} className="text-[#B45309]" />,
      color: 'amber',
      category: 'Fintech & Digital Lenders',
      title: 'Open Banking Enrichment',
      image: 'https://plus.unsplash.com/premium_photo-1661301075857-63868ae88c00?auto=format&fit=crop&w=1200&q=80',
      challenge: 'Raw transaction data lacks context for decision-making',
      solution: 'Structured enrichment with merchant, category, and behavior tagging',
      impact: [
        'Instant underwriting',
        'Personalized product offers',
        'Enhanced customer insights'
      ]
    }
  ];

  const colorMap = {
    teal: { bg: 'bg-[#0E7490]/10', text: 'text-[#0E7490]', dot: 'bg-[#0E7490]' },
    purple: { bg: 'bg-[#4F46E5]/10', text: 'text-[#4F46E5]', dot: 'bg-[#4F46E5]' },
    blue: { bg: 'bg-[#1A56DB]/10', text: 'text-[#1A56DB]', dot: 'bg-[#1A56DB]' },
    amber: { bg: 'bg-[#B45309]/10', text: 'text-[#B45309]', dot: 'bg-[#B45309]' },
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Dark Navy */}
      <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-[#060D1B] via-[#0A1628] to-[#0d1f3c]">
        <div className="max-w-[1200px] mx-auto relative z-10">
          <div className="text-center" data-testid="use-cases-header">
            <h1 className="text-h1 text-white mb-6">Use Cases</h1>
            <p className="text-body-lg text-[#6B7280] max-w-3xl mx-auto">
              Transforming BFSI Operations: Real-world applications driving measurable impact across banking, 
              lending, and financial services
            </p>
          </div>
        </div>
      </section>

      {/* Use Cases Grid */}
      <section className="section-padding px-6 bg-[#EFF6FF]">
        <div className="max-w-[1200px] mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="text-h3 text-[#0A1628] mb-4">Where Cognifin Creates Measurable Impact</h2>
            <p className="text-body-lg text-[#6B7280] leading-relaxed">
              Explore how financial institutions apply Cognifin across underwriting, fraud control, and open banking workflows.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {useCases.map((useCase, index) => {
              const colors = colorMap[useCase.color];
              return (
                <Card key={index} className="bg-white border-[#D1D5DB] rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all" data-testid={`use-case-${index}`}>
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={useCase.image}
                      alt={useCase.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`p-2 ${colors.bg} rounded-lg`}>
                        {useCase.icon}
                      </div>
                      <span className={`text-sm ${colors.text} font-medium`}>{useCase.category}</span>
                    </div>
                    <CardTitle className="text-[#0A1628] text-2xl">{useCase.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-[#0A1628] font-semibold mb-2">Challenge</h4>
                        <p className="text-[#6B7280] text-sm">{useCase.challenge}</p>
                      </div>
                      <div>
                        <h4 className="text-[#0A1628] font-semibold mb-2">Solution</h4>
                        <p className="text-[#6B7280] text-sm">{useCase.solution}</p>
                      </div>
                      <div>
                        <h4 className="text-[#0A1628] font-semibold mb-2">Impact</h4>
                        <ul className="space-y-2">
                          {useCase.impact.map((item, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm text-[#374151]">
                              <span className={`w-1.5 h-1.5 ${colors.dot} rounded-full`}></span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Journey Section */}
          <div className="bg-[#0A1628] rounded-xl p-8 lg:p-12">
            <h2 className="text-h3 text-white mb-8 text-center">The 4-Step Journey</h2>
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { num: '1', title: 'Ingestion', desc: 'Agents capture docs, vision, and voice in field or branch' },
                { num: '2', title: 'Reasoning', desc: 'Swarm logic parses data, flags anomalies, and checks policy' },
                { num: '3', title: 'Synthesis', desc: 'Automated CAM generation with 80+ credit risk signals' },
                { num: '4', title: 'Approval', desc: 'Human review of credit-ready file with override control' }
              ].map((step, index) => (
                <div key={step.num} className="text-center">
                  <div className="text-5xl font-bold text-[#1A56DB] mb-4">{step.num}</div>
                  <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-[#6B7280] text-sm">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding px-6 bg-gradient-to-r from-[#0A1628] to-[#1A56DB]">
        <div className="max-w-[1200px] mx-auto text-center">
          <h2 className="text-h2 text-white mb-6">
            See These Use Cases in Action
          </h2>
          <p className="text-body-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Discover how Cognifin can transform your specific credit operations
          </p>
          <Button 
            asChild 
            size="lg" 
            className="bg-white hover:bg-white/90 text-[#0A1628] font-bold text-lg px-10 h-14 btn-hover-lift shadow-xl"
          >
            <Link to="/contact">Schedule a Demo</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default UseCases;
